'use client';
import React, { CSSProperties, ReactNode } from 'react';
import { ModalProps } from '@mui/material';
import { FilledButton, TextButton } from '../buttons';
import { useTranslations } from 'next-intl';
import { ButtonRow, EllipsedHeadline, ModalContainer, ModalContent, WrapHeadline } from './styles';
import { useBreakpoint } from '../../hooks';
import { isCompact } from '../../types';

interface ModalDialogProps extends ModalProps {
    title?: string;
    showFullTitle?: boolean;
    cancelText?: string;
    submitText?: string;
    isSubmitDisabled?: boolean;
    hideCancel?: boolean;
    modalContentStyle?: CSSProperties;
    submitIcon?: ReactNode;
    onClose: () => void;
    onSubmit: () => void;
}

const ModalDialog = ({
    title,
    submitText,
    cancelText,
    isSubmitDisabled,
    onClose,
    onSubmit,
    submitIcon,
    modalContentStyle,
    children,
    hideCancel,
    showFullTitle,
    ...props
}: ModalDialogProps) => {
    const t = useTranslations('dialog');
    const breakPoint = useBreakpoint();
    const isCompactLayout = isCompact(breakPoint);

    return (
        <ModalContainer
            onClose={onClose}
            // prevents click propagation on backdrop click
            onClick={(e) => e.stopPropagation()}
            {...props}
        >
            <ModalContent style={modalContentStyle}>
                {title &&
                    showFullTitle === true ? (
                    <WrapHeadline>{title}</WrapHeadline>
                ) : (
                    <EllipsedHeadline>{title}</EllipsedHeadline>
                )}
                {children}
                <ButtonRow isCompact={isCompactLayout}>
                    {hideCancel ? null : (
                        <TextButton
                            label={cancelText ?? t('cancel')}
                            onClick={(e) => {
                                e.stopPropagation();
                                onClose();
                            }}
                        />
                    )}
                    <FilledButton
                        icon={submitIcon}
                        label={submitText ?? t('ok')}
                        onClick={(e) => {
                            e.stopPropagation();
                            onSubmit();
                        }}
                        disabled={isSubmitDisabled}
                    />
                </ButtonRow>
            </ModalContent>
        </ModalContainer>
    );
};

export { ModalDialog };
export type { ModalDialogProps };
